import { Box, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useLazyQuery } from "@apollo/client";
import { useSetRecoilState, useRecoilValue } from "recoil";
import { useLocation } from "react-router-dom";

import { FeedItem } from "./FeedItem";
import { ClipContext } from "../@types";
import { FeedViewModel } from "./ViewModel";
import { TClip } from "MVVM/@types";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import { analytics } from "MVVM/Analytics";
import { EmptyList } from "../EmptyList";
import { GET_CLIP } from "MVVM/GraphQL/clip";
import { ViewableClip, LoadingClipId } from "MVVM/State/clip";

const Feed = () => {
  const location = useLocation<{ context: ClipContext }>();
  const { data, error, loading } = FeedViewModel(location?.state?.context);
  const currentClip = useRecoilValue(ViewableClip);
  const setViewableClip = useSetRecoilState(ViewableClip);
  const setLoadingClipId = useSetRecoilState(LoadingClipId);
  const [getClip] = useLazyQuery(GET_CLIP, { fetchPolicy: "network-only" });

  const handleClipClick = async (clip: TClip) => {
    try {
      analytics.track("Clip Page - Feed Clip Clicked", {});

      setLoadingClipId(clip.shareId);

      const result = await getClip({ variables: { id: clip.shareId } });

      if (result.data?.clip) {
        setViewableClip(result.data.clip);
        analytics.page("Clip Experience Page", {});

        window.history.replaceState(
          {},
          "",
          `/clip/${result.data.clip.shareId}${window.location.search}`,
        );
      }
    } catch (e) {
    } finally {
      setLoadingClipId(null);
    }
  };

  if (loading)
    return (
      <FeedContainer>
        {Array(8)
          .fill("")
          .map((_, index) => (
            <LoadingSkeleton
              height={100}
              style={{ margin: "6px 0" }}
              key={index}
            />
          ))}
      </FeedContainer>
    );

  if (error || !data)
    return (
      <FeedContainer>
        <Text fontWeight={500}>Something went wrong.</Text>
      </FeedContainer>
    );

  if (!data.clipsRecommended.length)
    return <EmptyList text="Nothing else to see" />;

  return (
    <FeedContainer>
      {data.clipsRecommended.map((clip: TClip) => {
        const isCurrentClip =
          currentClip && clip.shareId === currentClip.shareId;

        return (
          <FeedItem
            clip={clip}
            key={clip._id}
            active={!!isCurrentClip}
            onClick={isCurrentClip ? undefined : () => handleClipClick(clip)}
          />
        );
      })}
    </FeedContainer>
  );
};

const FeedContainer = styled(Box)`
  background: ${({ theme }) => theme.colors.darkerNed};
  height: 100%;
  overflow: auto;
  padding: 12px;
  width: 100%;
`;

export { Feed };
